import { Fragment, useEffect, useRef, useState } from "react";

import { ACCESS_REQUIRED_EVENT, kernel } from "../client.js";
import AccessRequired from "./AccessRequired.jsx";
import Loading from "./Loading.jsx";

/**
 * What an app is wrapped in so a person who lacks access sees the
 * missing-permissions screen instead of the app (LO-209).
 *
 * ```jsx
 * <AccessGate appType="app-vendor-spend" title="Vendor spend" token={session.token}
 *             onSignOut={signOut}>
 *   <App token={session.token} />
 * </AccessGate>
 * ```
 *
 * ## Two ways in, so no app can skip it
 *
 * **Up front.** On load it asks `GET /apps/{app}/my-access`. The kernel's answer
 * is `ok`, and the app opens; or it is not, and the screen is shown and the app
 * is never mounted — it cannot render "less" or flash a half page, because it
 * does not render.
 *
 * **From any data call.** It also listens for the kit client's
 * `kernel:access-required` event, which `kernel`, `kernelPost` and
 * `kernelDelete` fire when the kernel answers `403 insufficient_access`. So an
 * app that never asks `my-access`, or whose access was withdrawn while it was
 * open, still ends in the same screen. The error is still thrown to the app's
 * own code; the event is how the *shell* hears.
 *
 * ## It fails open, on purpose
 *
 * If `my-access` itself cannot be read (an older kernel without the route, a
 * network failure) the app opens. That is not a hole: the kernel decides on
 * every data call and refuses the same way, and the second route above turns
 * that refusal into the screen. What this gate must not do is lock people out
 * of an app because a *convenience* check failed.
 *
 * ## The sandbox
 *
 * A sandbox persona is not exempt: it gets this screen, with the sandbox banner
 * still on, because the banner is outside the app's tree. The gate also does
 * **not** use `useSandboxPersona`, which would cancel the reload a persona
 * switch causes. The reload is what gives the app a token for the new persona;
 * a gate that suppressed it would have to hand the app one, and it cannot.
 *
 * ## Getting back in
 *
 * When the screen says the person now holds everything, the app is mounted
 * afresh (a new `key`), so it reads what it could not before rather than
 * resuming with an error in its state.
 */
/** How soon after letting the app back in a new refusal is a contradiction. */
const RETRY_GUARD_MS = 5000;

export default function AccessGate({
  appType,
  title,
  token,
  onSignOut,
  pollMs,
  children,
}) {
  const [state, setState] = useState({ phase: "checking" });
  const [epoch, setEpoch] = useState(0);

  // The token can be renewed under the app; asking again for that would
  // unmount it, so the check reads the latest one and runs once per app.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // When the screen last let the app back in. A kernel that says "yes" to
  // `my-access` and then refuses the app's first read would otherwise be a loop
  // the person cannot leave: in, refused, screen, in, refused. A refusal this
  // soon after being let in is shown as that contradiction instead of retried.
  const reopened = useRef(0);
  useEffect(() => {
    let live = true;
    kernel(tokenRef.current)(`/apps/${encodeURIComponent(appType)}/my-access`)
      .then((answer) => {
        if (!live) return;
        setState((was) => {
          if (was.phase === "blocked") return was;
          if (answer && answer.ok === false) {
            return {
              phase: "blocked",
              missing: answer.missing,
              requests: Array.isArray(answer.requests) ? answer.requests : [],
              canRequest: answer.can_request !== false,
            };
          }
          return { phase: "open" };
        });
      })
      .catch((failure) => {
        // Fail open: the kernel refuses on the data calls whatever this said.
        if (live) setState((was) => (was.phase === "blocked" ? was : { phase: "open", checkFailed: failure }));
      });
    return () => {
      live = false;
    };
  }, [appType]);

  useEffect(() => {
    const heard = (event) => {
      const body = event.detail?.body;
      // Another app's refusal is not this app's screen.
      if (body?.app_type && body.app_type !== appType) return;
      setState({
        phase: "blocked",
        missing: body?.missing,
        detail: body?.detail,
        doesNotProve: body?.does_not_prove,
        canRequest: body?.can_request !== false,
        requests: undefined,
        refusedAnyway: Date.now() - reopened.current < RETRY_GUARD_MS,
      });
    };
    window.addEventListener(ACCESS_REQUIRED_EVENT, heard);
    return () => window.removeEventListener(ACCESS_REQUIRED_EVENT, heard);
  }, [appType]);

  if (state.phase === "checking") {
    return <Loading label="Checking your access" minHeight="60vh" />;
  }

  if (state.phase === "blocked") {
    return (
      <AccessRequired
        appType={appType}
        title={title}
        missing={state.missing}
        requests={state.requests}
        detail={state.detail}
        doesNotProve={state.doesNotProve}
        canRequest={state.canRequest}
        token={token}
        pollMs={pollMs}
        onSignOut={onSignOut}
        refusedAnyway={state.refusedAnyway}
        onAllowed={
          state.refusedAnyway
            ? undefined
            : () => {
                reopened.current = Date.now();
                setEpoch((n) => n + 1);
                setState({ phase: "open" });
              }
        }
      />
    );
  }

  return <Fragment key={epoch}>{children}</Fragment>;
}
